import { useQuery } from "@tanstack/react-query";

import { api } from "../../api/client";
import { RbacApi, type Assignment } from "../rbac/api";
import { useUsersById } from "../rbac/useUsersById";

interface PersonNode {
  id: string;
  fullName: string;
  position: string | null;
  reportsTo: string | null;
  reportsToType: "user" | "person" | null;
}

/**
 * Organogram — visualizes the reports-to chain across all users.
 *
 * Sourced from existing Assignments. Each assignment has a reportsToUid; we
 * build a tree by user, with anyone unreferenced as a tree root. Renders
 * recursively as nested cards. Cycles are guarded by a visited-set.
 */
export function Organogram() {
  const { byId, name } = useUsersById();
  const assignments = useQuery({ queryKey: ["assignments-all"], queryFn: () => RbacApi.listAssignments() });
  const people = useQuery({ queryKey: ["people"], queryFn: () => api<{ items: PersonNode[] }>("/people") });

  const peopleById = new Map<string, PersonNode>();
  for (const p of people.data?.items ?? []) peopleById.set(`person:${p.id}`, p);

  // Build child map: parentKey -> [childKey...]
  // Keys are namespaced by entity type so people and users coexist:
  //   user:<uid>   → a registered user
  //   person:<id>  → a community contact (no platform account)
  const children = new Map<string, Set<string>>();
  const reporting = new Set<string>();

  for (const a of (assignments.data?.items ?? []) as Assignment[]) {
    if (!a.reportsToUid) continue;
    const childKey = `user:${a.userId}`;
    const parentKey = `user:${a.reportsToUid}`;
    const arr = children.get(parentKey) ?? new Set<string>();
    arr.add(childKey);
    children.set(parentKey, arr);
    reporting.add(childKey);
  }
  for (const p of people.data?.items ?? []) {
    if (!p.reportsTo || !p.reportsToType) continue;
    const childKey = `person:${p.id}`;
    const parentKey = `${p.reportsToType}:${p.reportsTo}`;
    const arr = children.get(parentKey) ?? new Set<string>();
    arr.add(childKey);
    children.set(parentKey, arr);
    reporting.add(childKey);
  }

  // Every entity that exists, namespaced.
  const allMentioned = new Set<string>();
  for (const [parent, kids] of children.entries()) {
    allMentioned.add(parent);
    for (const k of kids) allMentioned.add(k);
  }
  for (const uid of byId.keys()) allMentioned.add(`user:${uid}`);
  for (const p of people.data?.items ?? []) allMentioned.add(`person:${p.id}`);

  const roots = [...allMentioned].filter((k) => !reporting.has(k));

  function labelFor(key: string): string {
    if (key.startsWith("user:")) return name(key.slice(5));
    if (key.startsWith("person:")) {
      const p = peopleById.get(key);
      return p ? p.fullName : key;
    }
    return key;
  }
  function subtitleFor(key: string): string | null {
    if (key.startsWith("person:")) {
      const p = peopleById.get(key);
      return p?.position ?? "Community contact";
    }
    return null;
  }

  return (
    <div>
      <header style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Organogram</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Reports-to chain across all users. Built from each user's <strong>Edit access</strong> assignments —
          set someone's "Reports to" there to position them in this chart.
        </p>
      </header>

      {assignments.isLoading && <p>Loading…</p>}
      {roots.length === 0 && !assignments.isLoading && (
        <div className="muted" style={{ padding: 16 }}>
          No users yet. Create some in <strong>Setup → Users</strong>.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {roots.map((key) => (
          <Node
            key={key}
            nodeKey={key}
            children={children}
            label={labelFor}
            subtitle={subtitleFor}
            depth={0}
            visited={new Set()}
          />
        ))}
      </div>
    </div>
  );
}

function Node({
  nodeKey, children, label, subtitle, depth, visited,
}: {
  nodeKey: string;
  children: Map<string, Set<string>>;
  label: (key: string) => string;
  subtitle: (key: string) => string | null;
  depth: number;
  visited: Set<string>;
}) {
  if (visited.has(nodeKey)) {
    return (
      <div className="muted" style={{ marginLeft: depth * 24, fontSize: 12, padding: 4 }}>
        ↩ cycle back to {label(nodeKey)} (skipped)
      </div>
    );
  }
  const next = new Set(visited);
  next.add(nodeKey);
  const kids = [...(children.get(nodeKey) ?? [])];
  const isPerson = nodeKey.startsWith("person:");
  const sub = subtitle(nodeKey);

  return (
    <div style={{ marginLeft: depth * 24 }}>
      <div className="list-card" style={{ minHeight: 0, padding: "10px 14px", borderLeft: isPerson ? "3px solid var(--warning)" : "3px solid var(--accent)" }}>
        <div className="list-card-title">
          {label(nodeKey)}
          {isPerson && (
            <span className="muted" style={{ fontSize: 10, marginLeft: 8, padding: "1px 6px", borderRadius: 999, border: "1px solid var(--border)" }}>
              community
            </span>
          )}
        </div>
        {sub && (
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>
        )}
        {kids.length > 0 && (
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {kids.length} direct report{kids.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
      {kids.length > 0 && (
        <div style={{ borderLeft: "2px solid var(--border)", marginLeft: 12, marginTop: 4, paddingLeft: 12 }}>
          {kids.map((k) => (
            <Node
              key={k}
              nodeKey={k}
              children={children}
              label={label}
              subtitle={subtitle}
              depth={depth + 1}
              visited={next}
            />
          ))}
        </div>
      )}
    </div>
  );
}
